import React from 'react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import {
  addUniversalMeasurement,
  getAllCompanies,
  getUniversalMeasurement
} from '../../services/api/implementation/impl';

const UniversalMeasurement = () => {
  const { collapsed } = useSidebar();
  const initialCompanyId =
    localStorage.getItem('companyId') || Cookies.get('companyId') || Cookies.get('company_id') || '';
  const [selectedCompanyId, setSelectedCompanyId] = React.useState(initialCompanyId);
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [newMeasurement, setNewMeasurement] = React.useState({
    name: '',
    per_unit: '',
    measurement_type: 'sq/ft'
  });

  const { data: companies = [] } = useQuery({
    queryKey: ['allCompaniesForUniversalMeasurement'],
    queryFn: getAllCompanies
  });

  React.useEffect(() => {
    if (selectedCompanyId) {
      return;
    }
    if (companies.length > 0) {
      const firstCompanyId = String(companies[0].id);
      setSelectedCompanyId(firstCompanyId);
      localStorage.setItem('companyId', firstCompanyId);
    }
  }, [companies, selectedCompanyId]);

  const {
    data: universalMeasurements = [],
    isLoading,
    isError,
    refetch: refetchUniversalMeasurements
  } = useQuery({
    queryKey: ['universalMeasurement', selectedCompanyId],
    queryFn: () => getUniversalMeasurement(selectedCompanyId),
    enabled: Boolean(selectedCompanyId)
  });

  const { mutate: handleAddUniversalMeasurement, isPending: isAddingMeasurement } = useMutation({
    mutationFn: (payload) => addUniversalMeasurement(payload),
    onSuccess: async () => {
      toast.success('Universal Measurement added successfully');
      setIsAddModalOpen(false);
      setNewMeasurement({ name: '', per_unit: '', measurement_type: 'sq/ft' });
      await refetchUniversalMeasurements();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to add universal measurement');
    }
  });

  const MEASUREMENT_TYPES = [
    { value: 'sq/ft', label: 'sq/ft' },
    { value: 'sq/mt', label: 'sq/mt' }
  ];

  const handleCompanyChange = (event) => {
    const companyId = event.target.value;
    setSelectedCompanyId(companyId);
    if (companyId) {
      localStorage.setItem('companyId', companyId);
    }
  };

  const handleMeasurementInputChange = (event) => {
    const { name, value } = event.target;
    setNewMeasurement((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenAddModal = () => {
    if (!selectedCompanyId) {
      toast.error('Please select a company first');
      return;
    }
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewMeasurement({ name: '', per_unit: '', measurement_type: 'sq/ft' });
  };

  const handleSubmitNewMeasurement = (event) => {
    event.preventDefault();

    if (!newMeasurement.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!newMeasurement.per_unit || Number(newMeasurement.per_unit) <= 0) {
      toast.error('Per unit must be greater than 0');
      return;
    }

    handleAddUniversalMeasurement({
      company_id: Number(selectedCompanyId),
      name: newMeasurement.name.trim(),
      per_unit: String(newMeasurement.per_unit),
      measurement_type: newMeasurement.measurement_type
    });
  };

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header data={{ name: 'Universal Measurement' }} backArrow={false} />
      </div>

      <div className="mt-[5.5rem] p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Universal Measurement</h2>

          <div className="mb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="w-full md:w-80">
              <label htmlFor="company-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Company
              </label>
              <select
                id="company-select"
                value={selectedCompanyId}
                onChange={handleCompanyChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select Company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.company_name || company.name || `Company ${company.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Button onClick={handleOpenAddModal}>Add Universal Measurement</Button>
            </div>
          </div>

          {!selectedCompanyId && (
            <p className="text-gray-600">
              Company is not selected. Please select a company to view universal measurements.
            </p>
          )}

          {selectedCompanyId && isLoading && (
            <p className="text-gray-600">Loading universal measurements...</p>
          )}

          {selectedCompanyId && isError && (
            <p className="text-red-500">Failed to load universal measurements.</p>
          )}

          {selectedCompanyId && !isLoading && !isError && universalMeasurements.length === 0 && (
            <p className="text-gray-600">No universal measurements found.</p>
          )}

          {selectedCompanyId && !isLoading && !isError && universalMeasurements.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Per Unit
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Measurement Type
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {universalMeasurements.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.per_unit ?? '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{item.measurement_type ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isAddModalOpen && (
        <Modal name="Add Universal Measurement" onClose={handleCloseAddModal} width={40}>
          <form onSubmit={handleSubmitNewMeasurement} className="space-y-4">
            <div>
              <label htmlFor="measurement-name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                id="measurement-name"
                type="text"
                name="name"
                value={newMeasurement.name}
                onChange={handleMeasurementInputChange}
                placeholder="Enter name"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="measurement-per-unit" className="block text-sm font-medium text-gray-700 mb-1">
                Per Unit
              </label>
              <input
                id="measurement-per-unit"
                type="number"
                name="per_unit"
                value={newMeasurement.per_unit}
                onChange={handleMeasurementInputChange}
                placeholder="Enter per unit value"
                min="0"
                step="0.00000001"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="measurement-type" className="block text-sm font-medium text-gray-700 mb-1">
                Measurement Type
              </label>
              <select
                id="measurement-type"
                name="measurement_type"
                value={newMeasurement.measurement_type}
                onChange={handleMeasurementInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                {MEASUREMENT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="pt-2 flex justify-end gap-3">
              <Button type="button" displayType="secondary" onClick={handleCloseAddModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={isAddingMeasurement}>
                Add
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
};

export default UniversalMeasurement;
